<template>
  <div class="aspect-video w-full">
    <vue-vimeo-player
        v-if=" isVisible "
        v-show="isPlayerReady"
        ref="playerRef"
        id="player"
        :video-id="getVideoSrc"
        :events-to-emit="playerEvents"
        @ready="onReady"
        @play="onPlay"
        allow="autoplay; fullscreen; picture-in-picture"
        mozallowfullscreen webkitallowfullscreen allowfullscreen
        :controls="true"/>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import Player from "@vimeo/player";
import { vueVimeoPlayer } from 'vue-vimeo-player';
import { reportVideoPlayEvent } from "@/composables/user-activity-monitor";

const props = defineProps<{ vimeoID: any; }>();
const isVisible = computed( () => !!props.vimeoID );
const getVideoSrc = computed( () => {
  if ( typeof props.vimeoID === 'string' ) {
    if ( props.vimeoID.includes( 'http' ) || props.vimeoID.includes( '.com' ) ) {
      const parts = props.vimeoID.split( '/' );
      return parts[ parts.length - 1 ];
    }
  }

  return props.vimeoID;
} );

const playerRef = ref();

const isPlayerReady = ref<boolean>( false );
const onReady = ( _playerInstance: Player ) => {
  playerRef.value.player = _playerInstance;
  isPlayerReady.value = true;
};

const playerEvents = [ 'play', 'pause', 'ended', 'error', 'loaded' ];

const hasPlayed = ref<boolean>( false );
const vimeoInfo = ref( {
  id: getVideoSrc.value,
  title: '',
  duration: 0
} );

const onPlay = () => {
  Promise.all( [
    playerRef.value.player.getVideoId(),
    playerRef.value.player.getVideoTitle(),
    playerRef.value.player.getDuration()
  ] ).then( ( [ id, title, duration ] ) => {
    vimeoInfo.value = { id, title, duration };
  } );
  if ( !hasPlayed.value ) {
    reportVideoPlayEvent( vimeoInfo.value.id );
  }
  hasPlayed.value = true;
};

watch( () => props.vimeoID, () => {
  hasPlayed.value = false;
} );

function pauseVideo() {
  playerRef.value.pause();
}

defineExpose( { pauseVideo } );
</script>

<style>
#player iframe {
  aspect-ratio: 16 / 9 !important;
  width: 100% !important;
  height: auto !important;
  border-radius: 0.5rem !important;
}
</style>
